import { createEffect, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"

const documentFrame = `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; font-src data: blob:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">
<style>html,body{margin:0;padding:0}body{width:max-content;min-width:100%;background:#eee}</style>
</head><body><main id="docx-preview-root"></main></body></html>`

async function documentBytes(blob: Blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  if (bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 3 && bytes[3] === 4) return bytes
  // Some legacy artifacts store base64 rather than the original ZIP bytes.
  const decoded = Uint8Array.from(atob(new TextDecoder().decode(bytes).trim()), (char) => char.charCodeAt(0))
  if (decoded[0] !== 0x50 || decoded[1] !== 0x4b || decoded[2] !== 3 || decoded[3] !== 4) throw new Error("Invalid DOCX")
  return decoded
}

export function DocxPreview(props: {
  blob: Blob
  title: string
  loading: string
  error: string
  zoomIn: string
  zoomOut: string
  scaleLabel: (percent: string) => string
}) {
  let frame!: HTMLIFrameElement
  const [state, setState] = createStore({ frameVersion: 0, loading: true, error: false, scale: 0.72, width: 816, height: 1056 })

  createEffect(() => {
    const blob = props.blob
    if (!state.frameVersion) return
    const document = frame.contentDocument
    const root = document?.getElementById("docx-preview-root")
    if (!document || !root) return
    let cancelled = false
    let observer: ResizeObserver | undefined
    // Each render owns its nodes, so a slow previous document cannot replace the current one.
    const styles = document.createElement("div")
    const body = document.createElement("div")
    root.replaceChildren(styles, body)
    setState({ loading: true, error: false, width: 816, height: 1056 })
    onCleanup(() => {
      cancelled = true
      observer?.disconnect()
      styles.remove()
      body.remove()
    })
    void (async () => {
      try {
        const [renderer, bytes] = await Promise.all([import("docx-preview"), documentBytes(blob)])
        if (cancelled) return
        await renderer.renderAsync(bytes, body, styles, {
          useBase64URL: true,
          renderAltChunks: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          renderHeaders: true,
          renderFooters: true,
          renderComments: false,
        })
        if (cancelled) return
        const resize = () => {
          if (cancelled) return
          setState({ width: Math.max(200, root.scrollWidth), height: Math.max(200, root.scrollHeight) })
        }
        resize()
        observer = new ResizeObserver(resize)
        observer.observe(root)
        setState("loading", false)
      } catch {
        if (!cancelled) {
          root.replaceChildren()
          setState({ loading: false, error: true })
        }
      }
    })()
  })

  const zoom = (direction: -1 | 1) =>
    setState("scale", (value) => Math.min(1.5, Math.max(0.5, Number((value + direction * 0.1).toFixed(2)))))

  return (
    <div class="overflow-hidden rounded-md border border-v2-border-border-base bg-v2-background-bg-base" aria-busy={state.loading}>
      <div class="flex items-center justify-between border-b border-v2-border-border-base px-3 py-2">
        <span class="text-12-regular text-v2-text-text-muted">{props.scaleLabel(`${Math.round(state.scale * 100)}%`)}</span>
        <div class="flex items-center gap-2">
          <button type="button" class="size-7 rounded-md border border-v2-border-border-base" aria-label={props.zoomOut}
            disabled={state.scale <= 0.5} onClick={() => zoom(-1)}>−</button>
          <button type="button" class="size-7 rounded-md border border-v2-border-border-base" aria-label={props.zoomIn}
            disabled={state.scale >= 1.5} onClick={() => zoom(1)}>+</button>
        </div>
      </div>
      <Show when={state.loading}><p role="status" class="p-4 text-13-regular text-v2-text-text-muted">{props.loading}</p></Show>
      <Show when={state.error}><p role="alert" class="p-4 text-13-regular text-v2-text-text-muted">{props.error}</p></Show>
      <div class="max-h-[720px] overflow-auto bg-v2-background-bg-layer-01 p-4" classList={{ hidden: state.error }}>
        <div style={{ width: `${state.width * state.scale}px`, height: `${state.height * state.scale}px` }}>
          <iframe
            ref={frame}
            title={props.title}
            sandbox="allow-same-origin"
            srcdoc={documentFrame}
            class="origin-top-left border-0"
            style={{ width: `${state.width}px`, height: `${state.height}px`, transform: `scale(${state.scale})`,
              visibility: state.loading || state.error ? "hidden" : "visible" }}
            onLoad={() => setState("frameVersion", (value) => value + 1)}
          />
        </div>
      </div>
    </div>
  )
}
